import React from 'react';
import { Repository } from '../../types/api';
import { FolderGit2, Tag, Clock, ExternalLink, RefreshCw } from 'lucide-react';
import clsx from 'clsx';

interface RepositoryCardProps {
  repository: Repository;
  onSync?: (fullName: string) => void;
  onClick?: (repository: Repository) => void;
}

export function RepositoryCard({ repository, onSync, onClick }: RepositoryCardProps) {
  const fullName = repository.fullName || `${repository.owner}/${repository.name}`;
  const lastSyncDate = repository.lastSync
    ? new Date(repository.lastSync).toLocaleDateString()
    : 'Never';

  const handleSync = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSync?.(fullName);
  };

  const handleClick = () => {
    onClick?.(repository);
  };

  return (
    <div
      className={clsx(
        'bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-shadow',
        onClick && 'cursor-pointer'
      )}
      onClick={handleClick}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-blue-50 rounded-lg">
            <FolderGit2 className="h-6 w-6 text-blue-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">{repository.name}</h3>
            <p className="text-sm text-gray-500">{repository.owner}</p>
          </div>
        </div>
        <button
          onClick={handleSync}
          className="p-2 rounded-md text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
          title="Sync repository"
        >
          <RefreshCw className="h-4 w-4" />
        </button>
      </div>

      {/* Metadata */}
      {repository.metadata && (
        <div className="flex items-center gap-4 mb-4 text-sm text-gray-600">
          {repository.metadata.language && (
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 bg-blue-500 rounded-full"></span>
              {repository.metadata.language}
            </span>
          )}
          {repository.metadata.defaultBranch && (
            <span>{repository.metadata.defaultBranch}</span>
          )}
          {repository.metadata.isPrivate && (
            <span className="px-2 py-0.5 bg-gray-100 rounded text-xs">Private</span>
          )}
        </div>
      )}

      {/* Tags */}
      {repository.tags && repository.tags.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {repository.tags.map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 rounded-md text-xs"
            >
              <Tag className="h-3 w-3" />
              {tag}
            </span>
          ))}
        </div>
      )}

      {/* Installed Features */}
      {repository.installedFeatures && Object.keys(repository.installedFeatures).length > 0 && (
        <div className="mb-4">
          <p className="text-xs font-medium text-gray-500 mb-2">Installed Features</p>
          <div className="flex flex-wrap gap-2">
            {Object.entries(repository.installedFeatures).map(([feature, version]) => (
              <span
                key={feature}
                className="px-2 py-1 bg-green-50 text-green-700 rounded text-xs"
              >
                {feature}@{version}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between pt-4 border-t border-gray-100">
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <Clock className="h-3 w-3" />
          Last sync: {lastSyncDate}
        </div>
        <a
          href={repository.url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700"
        >
          View on GitHub
          <ExternalLink className="h-3 w-3" />
        </a>
      </div>
    </div>
  );
}
